import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'react-bootstrap-icons';
import DashboardLayout from '../components/DashboardLayout';
import ComplaintsTable from '../components/ComplaintsTable';
import ComplaintSolutionModal from '../components/ComplaintSolutionModal';
import ComplaintViewModal from '../components/ComplaintViewModal';
import { apiRequest } from '../utils/api';
import { getPreferredEmployeeId } from '../utils/employeeIdentity';
import '../assets/styles/complaints.css';

const PAGE_SIZE = 8;
const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'solved', label: 'Solved' },
  { value: 'completed', label: 'Completed' },
];

const formatEnquiry = (item) => ({
  id: item?.id,
  date: item?.createdAt ?? item?.updatedAt ?? item?.resolvedAt ?? null,
  name: item?.employee?.name ?? '—',
  mobile: item?.employee?.phone ?? '—',
  employeeCode: getPreferredEmployeeId(item?.employee) || null,
  message: item?.message ?? '',
  status: item?.status ?? 'pending',
  subject: item?.subject ?? null,
  resolutionNote: item?.resolutionNote ?? null,
  resolvedAt: item?.resolvedAt ?? null,
  createdAt: item?.createdAt ?? null,
  updatedAt: item?.updatedAt ?? null,
});

const SuggestionsComplaints = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [complaints, setComplaints] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeComplaint, setActiveComplaint] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewComplaint, setViewComplaint] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const pageCount = useMemo(() => Math.max(1, totalPages), [totalPages]);

  const loadComplaints = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: PAGE_SIZE.toString(),
      });

      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }

      if (searchTerm) {
        params.set('search', searchTerm);
      }

      const response = await apiRequest(
        `/api/admin/employee-enquiries?${params.toString()}`,
        {},
        navigate,
      );

      const items = Array.isArray(response.data) ? response.data : [];
      const meta = response.meta ?? {};
      const nextTotalPages = Math.max(
        1,
        Number.isFinite(Number(meta.totalPages)) && Number(meta.totalPages) > 0
          ? Number(meta.totalPages)
          : Math.ceil(items.length / PAGE_SIZE) || 1,
      );

      const formatted = items.map((item) => formatEnquiry(item));

      setTotalPages(nextTotalPages);
      setComplaints(formatted);

      if (nextTotalPages > 0 && page > nextTotalPages) {
        setPage(nextTotalPages);
      }
    } catch (requestError) {
      setError(requestError.message || 'Unable to load enquiries');
      setComplaints([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [navigate, page, searchTerm, statusFilter]);

  useEffect(() => {
    loadComplaints();
  }, [loadComplaints, refreshToken]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleResetFilters = () => {
    setStatusFilter('all');
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleEdit = (complaint) => {
    if (!complaint) {
      setActiveComplaint(null);
      return;
    }

    const nextActive = complaints.find((item) => String(item.id) === String(complaint.id)) || complaint;
    setModalError(null);
    setActiveComplaint(nextActive);
  };

  const handleCloseModal = () => {
    if (modalSubmitting) {
      return;
    }

    setModalError(null);
    setActiveComplaint(null);
  };

  const handleSubmitSolution = async ({ id, status, resolutionNote, resolvedDate }) => {
    try {
      setModalSubmitting(true);
      setModalError(null);

      const response = await apiRequest(
        `/api/admin/employee-enquiries/${id}`,
        {
          method: 'PATCH',
          body: {
            status,
            resolutionNote,
            resolvedAt: resolvedDate,
          },
        },
        navigate,
      );

      const updated = formatEnquiry(response.data ?? {});

      setComplaints((prev) => {
        const matchesFilter =
          statusFilter === 'all' || String(updated.status) === String(statusFilter);
        const exists = prev.some((item) => String(item.id) === String(updated.id));

        if (!matchesFilter) {
          return prev.filter((item) => String(item.id) !== String(updated.id));
        }

        if (!exists) {
          return [updated, ...prev];
        }

        return prev.map((item) => (String(item.id) === String(updated.id) ? updated : item));
      });

      if (viewModalOpen && String(viewComplaint?.id) === String(updated.id)) {
        setViewComplaint(updated);
      }

      setActiveComplaint(null);
    } catch (requestError) {
      setModalError(requestError.message || 'Failed to update enquiry');
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleView = async (id) => {
    if (!id) {
      return;
    }

    try {
      setViewModalOpen(true);
      setViewLoading(true);
      setViewError(null);
      setViewComplaint(null);

      const response = await apiRequest(`/api/admin/employee-enquiries/${id}`, {}, navigate);
      setViewComplaint(formatEnquiry(response.data ?? {}));
    } catch (requestError) {
      setViewError(requestError.message || 'Failed to load enquiry');
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseViewModal = () => {
    if (viewLoading) {
      return;
    }
    setViewModalOpen(false);
    setViewComplaint(null);
    setViewError(null);
  };

  const actionButtonGroupClass = [
    'd-flex',
    'flex-column',
    'flex-md-row',
    'justify-content-md-end',
    'align-items-stretch',
    'align-items-md-center',
    'gap-3',
    'mb-4',
  ].join(' ');

  const paginatedRows = useMemo(() => complaints, [complaints]);

  return (
    <DashboardLayout title="Suggestions / Complaints">
      <div className="complaints-page container-fluid py-4">
        <div className={actionButtonGroupClass}>
          <button
            type="button"
            className="complaints-pill-button d-flex align-items-center justify-content-center"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          <button
            type="button"
            className="complaints-pill-button secondary d-flex align-items-center justify-content-center"
            onClick={() => setRefreshToken((value) => value + 1)}
          >
            Refresh
          </button>
        </div>

        <div className="complaints-filters shadow-sm mb-4">
          <form onSubmit={handleSearchSubmit}>
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label text-muted small fw-semibold text-uppercase">Status</label>
                <select
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                    setPage(1);
                  }}
                  className="form-select complaints-select"
                >
                  {STATUS_FILTERS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label text-muted small fw-semibold text-uppercase">Search</label>
                <div className="complaints-search-control">
                  <Search className="search-icon" size={18} />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search by employee name, mobile number or message"
                    className="form-control complaints-input"
                  />
                </div>
              </div>

              <div className="col-md-4 d-flex flex-column flex-md-row gap-2">
                <button type="submit" className="complaints-filter-apply w-100">
                  Apply Filters
                </button>
                <button
                  type="button"
                  className="complaints-filter-reset w-100"
                  onClick={handleResetFilters}
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>

        <ComplaintsTable
          rows={paginatedRows}
          loading={loading}
          error={error}
          page={page}
          pageCount={pageCount}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onEdit={handleEdit}
          onView={handleView}
        />
      </div>

      {activeComplaint && (
        <ComplaintSolutionModal
          complaint={activeComplaint}
          submitting={modalSubmitting}
          error={modalError}
          onClose={handleCloseModal}
          onSubmit={handleSubmitSolution}
        />
      )}

      <ComplaintViewModal
        open={viewModalOpen}
        complaint={viewComplaint}
        loading={viewLoading}
        error={viewError}
        onClose={handleCloseViewModal}
      />
    </DashboardLayout>
  );
};

export default SuggestionsComplaints;
